import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Play, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
const hms = (s) => {
  const t = Math.floor(s);
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return `${h ? `${h}h` : ""}${String(m).padStart(h ? 2 : 1, "0")}min${String(sec).padStart(2, "0")}`;
};

const usePositionSaver = (slug, videoId) => {
  const last = useRef({ pos: 0, sent: 0 });
  const send = (pos) => {
    last.current.sent = Date.now();
    api(`/membre/seminaires/${slug}/videos/${videoId}/position`, { method: "POST", body: { position: pos } }).catch(() => {});
  };
  const report = useCallback((pos, force = false) => {
    last.current.pos = pos;
    if (force || Date.now() - last.current.sent > 10000) send(pos);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (last.current.pos) send(last.current.pos); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return report;
};

const NativeVideo = ({ video, slug, start, report, noFullscreen }) => {
  const ref = useRef(null);
  const [src, setSrc] = useState(video.player.src);
  const resumeAt = useRef(start);
  const retries = useRef(0);
  const onError = async () => {
    if (retries.current >= 3) return;
    retries.current += 1;
    resumeAt.current = ref.current?.currentTime || 0;
    try {
      const d = await api(`/membre/seminaires/${slug}`);
      const v = (d.videos || []).find((x) => x.id === video.id);
      if (v?.player?.src) setSrc(v.player.src);
    } catch { /* accès retiré */ }
  };
  const onLoaded = () => {
    if (resumeAt.current && ref.current) {
      ref.current.currentTime = resumeAt.current;
      resumeAt.current = 0;
      ref.current.play().catch(() => {});
    }
  };
  return (
    <video ref={ref} src={src} controls autoPlay data-testid="replay-player" className="w-full h-full"
      onError={onError} onLoadedMetadata={onLoaded} onPlaying={() => { retries.current = 0; }}
      onTimeUpdate={(e) => report(e.currentTarget.currentTime)} onPause={(e) => report(e.currentTarget.currentTime, true)}
      controlsList={noFullscreen ? "nodownload nofullscreen" : "nodownload"} disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()} />
  );
};

const EmbedVideo = ({ video, titre, start, report, noFullscreen }) => {
  const ref = useRef(null);
  const yt = video.player.src.includes("youtube");
  const s = Math.floor(start);
  const src = yt
    ? `${video.player.src}?enablejsapi=1&autoplay=1&start=${s}&origin=${encodeURIComponent(window.location.origin)}`
    : `${video.player.src}?autoplay=1${s ? `#t=${s}s` : ""}`;

  useEffect(() => {
    const target = yt ? "https://www.youtube-nocookie.com" : "https://player.vimeo.com";
    const post = (msg) => ref.current?.contentWindow?.postMessage(JSON.stringify(msg), target);
    const onMsg = (e) => {
      if (e.source !== ref.current?.contentWindow) return;
      let d;
      try { d = typeof e.data === "string" ? JSON.parse(e.data) : e.data; } catch { return; }
      if (yt) {
        if (d?.event === "infoDelivery" && typeof d.info?.currentTime === "number") report(d.info.currentTime);
      } else if (d?.event === "ready") {
        post({ method: "addEventListener", value: "playProgress" });
        post({ method: "addEventListener", value: "pause" });
      } else if (d?.event === "playProgress") {
        report(d.data.seconds);
      } else if (d?.event === "pause" && d.data) {
        report(d.data.seconds, true);
      }
    };
    window.addEventListener("message", onMsg);
    const onLoad = () => { if (yt) post({ event: "listening", id: video.id, channel: "widget" }); };
    const el = ref.current;
    el?.addEventListener("load", onLoad);
    return () => { window.removeEventListener("message", onMsg); el?.removeEventListener("load", onLoad); };
  }, [yt, video.id, report]);

  return (
    <iframe ref={ref} src={src} title={`Replay — ${titre}`} data-testid="replay-player" className="w-full h-full"
      allow={noFullscreen ? "autoplay" : "autoplay; fullscreen"} allowFullScreen={!noFullscreen} />
  );
};

const Watermark = ({ text }) => {
  const [pos, setPos] = useState({ top: 12, left: 8 });
  useEffect(() => {
    const t = setInterval(() => setPos({ top: 8 + Math.random() * 70, left: 4 + Math.random() * 50 }), 20000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden select-none" data-testid="replay-watermark">
      <span className="absolute text-white/40 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-1000"
        style={{ top: `${pos.top}%`, left: `${pos.left}%`, textShadow: "0 1px 2px rgba(0,0,0,.6)" }}>
        {text}
      </span>
    </div>
  );
};

const Player = ({ video, titre, slug, filigrane }) => {
  const [playing, setPlaying] = useState(false);
  const [start, setStart] = useState(0);
  const report = usePositionSaver(slug, video.id);
  const resume = video.position > 5 ? video.position : 0;
  const launch = (from) => {
    setStart(from);
    setPlaying(true);
    api(`/membre/seminaires/${slug}/videos/${video.id}/vue`, { method: "POST" }).catch(() => {});
  };
  if (!playing) {
    return (
      <div className="flex flex-col items-center gap-4">
        <button data-testid="replay-play-button" onClick={() => launch(resume)}
          className="group flex flex-col items-center gap-4" aria-label="Lire le replay">
          <span className="h-20 w-20 rounded-full bg-cfhtb-primary flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-cfhtb-secondary transition-all">
            <Play className="h-9 w-9 text-white ml-1" fill="currentColor" />
          </span>
          <span className="text-white/80 text-sm font-medium">
            {resume ? `Reprendre à ${hms(resume)}` : video.titre}
          </span>
        </button>
        {resume > 0 && (
          <button data-testid="replay-restart-button" onClick={() => launch(0)}
            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors">
            <RotateCcw className="h-3.5 w-3.5" /> Reprendre depuis le début
          </button>
        )}
      </div>
    );
  }
  const props = { video, slug, titre, start, report, noFullscreen: !!filigrane };
  return video.player.kind === "video" ? <NativeVideo {...props} /> : <EmbedVideo {...props} />;
};

const FullscreenButton = ({ target }) => (
  <button data-testid="replay-fullscreen-button" aria-label="Plein écran"
    onClick={() => (document.fullscreenElement ? document.exitFullscreen() : target.current?.requestFullscreen?.())}
    className="absolute top-3 right-3 z-20 h-9 w-9 rounded-md bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors">
    <Maximize className="h-4 w-4" />
  </button>
);

export const ReplayPlayer = ({ videos, titre, slug, filigrane }) => {
  const [currentId, setCurrentId] = useState(videos[0].id);
  const current = videos.find((v) => v.id === currentId) || videos[0];
  const multi = videos.length > 1;
  const box = useRef(null);

  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden" data-testid="replay-section">
      <div className={multi ? "lg:grid lg:grid-cols-3" : ""}>
        <div className={multi ? "lg:col-span-2" : ""}>
          <div ref={box} className="aspect-video bg-cfhtb-heading relative flex items-center justify-center">
            <Player key={current.id} video={current} titre={titre} slug={slug} filigrane={filigrane} />
            {filigrane && <Watermark text={filigrane} />}
            {filigrane && <FullscreenButton target={box} />}
          </div>
          <div className="px-5 py-3 text-xs text-cfhtb-text" data-testid="replay-current-info">
            <span className="font-medium text-cfhtb-heading">{current.titre}</span>
            {current.expire_le ? ` · Accessible jusqu'au ${fmt(current.expire_le)}` : " · Sans limite de durée"}
          </div>
        </div>
        {multi && (
          <ul className="border-t lg:border-t-0 lg:border-l border-slate-100 divide-y divide-slate-100 lg:max-h-[28rem] overflow-y-auto" data-testid="replay-video-list">
            {videos.map((v, i) => {
              const active = v.id === current.id;
              return (
                <li key={v.id}>
                  <button data-testid={`replay-video-item-${i}`} onClick={() => setCurrentId(v.id)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${active ? "bg-cfhtb-bg" : "hover:bg-cfhtb-bg/50"}`}>
                    <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${active ? "bg-cfhtb-primary text-white" : "bg-slate-100 text-cfhtb-secondary"}`}>
                      {active ? <Play className="h-3.5 w-3.5 ml-0.5" fill="currentColor" /> : i + 1}
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-sm ${active ? "font-semibold text-cfhtb-primary" : "font-medium text-cfhtb-heading"}`}>{v.titre}</span>
                      <span className="block text-xs text-cfhtb-text/80 mt-0.5">
                        {v.expire_le ? `Jusqu'au ${fmt(v.expire_le)}` : "Sans limite"}
                        {v.position > 5 ? ` · arrêté à ${hms(v.position)}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
